import { cn } from '@/lib/utils';

import Badge from '@/components/atoms/badge';
import BadgeStrapi, {
  BadgeStrapiProps,
} from '@/components/atoms/badge/BadgeStrapi';
import { Text } from '@/components/atoms/Text';
import { gradientDecodoBorderStyling } from '@/styles/gradient';

import PricingCardCTA from '@/blocks/PricingCards/PricingCardCTA';
import { pricingCardContainerStyles } from './styles';

export type PricingCardProps = {
  id: number;
  title: string;
  discount?: string;
  discountLabel?: string;
  price: number;
  priceOld?: number;
  pricePer: 'IP' | 'GB';
  priceTotal?: number;
  priceTotalOld?: number;
  priceTotalPrefixText?: string;
  priceTotalPostfixText?: string;
  buttonText: string;
  buttonVariant?: 'filled' | 'outlined';
  url: string;
  outlineColor?: 'grey' | 'black' | 'brand' | 'gradient';
  outlineText?: string;
  ctaLeftSection?: React.ReactNode;
  ctaRightSection?: React.ReactNode;
  showMoneyBack?: boolean;
  alternativeFootnote?: string;
  alternativeFootnoteColor?: 'brand' | 'gray' | 'lavender';
  topBadge?: BadgeStrapiProps;
};

export const outlineClasses = {
  grey: '',
  black: '', // remove from strapi after rebranding
  brand: 'border-brand-300 dark:border-brand-500 dark:bg-brand-975 bg-brand-50',
  gradient: `${gradientDecodoBorderStyling({
    color: 'gradient-main',
    borderRadius: 'rounded',
    borderWidth: 'p-px',
  })}`,
} as const;

export default function PricingCard(props: PricingCardProps) {
  const {
    id,
    title,
    discount,
    discountLabel,
    price,
    priceOld,
    pricePer,
    priceTotal,
    priceTotalPrefixText,
    priceTotalPostfixText,
    priceTotalOld,
    buttonText,
    buttonVariant,
    url,

    outlineColor = 'grey',
    outlineText,

    ctaLeftSection,
    ctaRightSection,

    alternativeFootnote,
    alternativeFootnoteColor,
    topBadge,
  } = props;

  const formattedPricePer = pricePer
    ?.replaceAll('-', ' ')
    ?.replaceAll('ww', '');

  return (
    <div
      data-tracker-component='card'
      className={cn(
        pricingCardContainerStyles,
        topBadge && 'dark:!bg-brand-975 !bg-brand-50',
        outlineClasses[outlineColor],
        !!outlineText && '!mt-[28px] md:mt-0 rounded-t-none'
      )}
    >
      {outlineText && (
        <span
          className={cn(
            'text-label dark:text-base-dark-white bg-base-white dark:bg-gray-dark-900 absolute -left-px -right-px top-0 z-[1] -translate-y-full rounded-t border py-1.5 text-center font-serif uppercase text-gray-950',
            outlineColor && outlineClasses[outlineColor]
          )}
        >
          {outlineText}
        </span>
      )}
      <div className='mb-4 flex w-full items-center justify-between'>
        <Text
          classNames={{
            wrapper: 'dark:text-base-dark-white font-serif text-h4',
          }}
        >
          {title}
        </Text>

        <div className={cn('flex gap-2')}>
          {topBadge && <BadgeStrapi block={topBadge} />}
          {(discount || discountLabel) && (
            <div className={cn('flex gap-2')}>
              <Badge color='success' variant='soft' size='l'>
                <>
                  {discountLabel} {discount}
                </>
              </Badge>
            </div>
          )}
        </div>
      </div>
      <div className='mb-4 flex items-baseline'>
        {!!priceOld && (
          <Text
            classNames={{
              wrapper:
                'font-serif line-through text-gray-500 text-h4 mr-3 font-normal',
            }}
          >
            ${priceOld}
          </Text>
        )}
        <div className='dark:text-base-dark-white  flex items-baseline align-middle'>
          <Text classNames={{ wrapper: 'font-serif text-h2' }}>
            ${price}
          </Text>
          <Text classNames={{ wrapper: 'leading-[3rem] font-serif text-h4' }}>
            /{formattedPricePer}
          </Text>
        </div>
      </div>
      <p className='text-body-m dark:text-gray-dark-400 mb-6 flex max-h-5 min-h-5 items-center justify-center gap-1'>
        {priceTotalPrefixText && <span>{priceTotalPrefixText}</span>}
        {priceTotal && priceTotalPostfixText && (
          <span className='flex gap-x-1'>
            {priceTotalOld && (
              <span className='text-error-600 text-body-m-bold line-through'>
                ${priceTotalOld}
              </span>
            )}
            <span className='text-body-m-bold'>${priceTotal}</span>
            {priceTotalPostfixText}
          </span>
        )}
      </p>

      <p
        className={cn(
          'text-label-s dark:bg-gray-dark-800 dark:text-gray-dark-400 text-gray-600 mb-3 max-h-5 min-h-5 w-full text-nowrap rounded bg-gray-50 p-1 text-center font-serif uppercase',
          (outlineColor === 'brand' || alternativeFootnoteColor === 'brand') &&
            'bg-brand-100 text-brand-500 dark:bg-brand-950 dark:text-brand-100',
          alternativeFootnoteColor === 'lavender' &&
            'bg-brand-50 text-gray-600 dark:bg-brand-975',
          !alternativeFootnote && 'opacity-0'
        )}
      >
        {alternativeFootnote}
      </p>

      <PricingCardCTA
        url={url}
        text={buttonText}
        variant={buttonVariant || 'filled'}
        color='primary'
        trackEventName='pricing_card_cta_click'
        trackId={`${title}-${url?.split('planId=')[1] || ''}`}
        trackExtra={{ id, price, priceTotal, title, url, buttonText }}
        leftSection={ctaLeftSection}
        rightSection={ctaRightSection}
        buttonClassNames={{
          root: 'group',
          inner: 'flex items-center justify-center gap-4',
          rightSection:
            'group-hover:translate-x-2 duration-300 ease-in-out transition',
        }}
      />
    </div>
  );
}
